/**
 * The rail pages' own top strip: search, company, alerts, password, sign out.
 *
 * Five controls, and each one leads somewhere real. Search queries the
 * candidate and client endpoints that already exist; the bell counts the same
 * `/alerts` payload AlertWatcher polls; the key opens the change-password
 * section that has always been on My Profile; sign out and the company picker
 * moved here from the rail foot rather than being duplicated.
 *
 * **The timezone control in the reference layout is still absent.** It is the
 * one thing on that strip with nothing behind it — the timezone is a server
 * constant, so a picker could only offer the value it already shows. Every
 * other control here changed from "nothing behind it" to "something behind it"
 * and arrived on that basis.
 *
 * On the rail pages only. AppLayout's own header still renders on every other
 * route, exactly as it did, and this replaces nothing there.
 */
import { useNavigate } from 'react-router-dom';
import { KeyRound, LogOut } from 'lucide-react';
import ActingCompanyPicker from './ActingCompanyPicker';
import AlertBell from './AlertBell';
import GlobalSearch from './GlobalSearch';
import { useImpersonation } from '../store/impersonation';
import { canSeeAllCompanies, useAuth } from '../store/auth';

export default function DashboardTopBar() {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();
  const viewing = useImpersonation((s) => s.target) !== null;

  return (
    <header className="flex flex-wrap items-center justify-end gap-2 border-b border-gray-200 bg-white px-5 py-2.5">
      <div className="mr-auto min-w-0 flex-1 sm:max-w-xs">
        <GlobalSearch />
      </div>

      {/* Hidden while viewing, for the reason the old header hid it: you are
          looking at their app, and offering your own controls inside it is how
          somebody changes a setting believing they are in their own account.
          Now genuinely reachable — an owner lands on /admin/home, which is a
          rail page, so this is no longer the dead control it was in the rail
          foot while owners were redirected away from the dashboard. */}
      {canSeeAllCompanies(user) && !viewing && <ActingCompanyPicker />}

      <AlertBell />

      {/* The change-password form lives on My Profile and is the only one.
          A second copy in a dialog here would be a second thing to keep in
          step with the password rules, so this is a link rather than a form. */}
      <button
        type="button"
        className="btn-ghost transition-colors duration-300 ease-out"
        onClick={() => navigate('/profile')}
        aria-label="Change your password"
        title="Change your password"
      >
        <KeyRound className="h-4 w-4" />
      </button>

      <button
        type="button"
        className="btn-ghost transition-colors duration-300 ease-out hover:text-red-600"
        onClick={async () => {
          await logout();
          navigate('/login', { replace: true });
        }}
        aria-label="Sign out"
        title="Sign out"
      >
        <LogOut className="h-4 w-4" />
      </button>
    </header>
  );
}
